import React, { useEffect, useState, useMemo, useRef } from "react";
import { Box, TextField, Button, TablePagination, Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeader1 from "../../components/common/PageHeader1";
import { useDispatch, useSelector } from "react-redux";
import { fetchRechargeReport } from "../../Redux/Reducers/CallAndChatReducer";
import axios from "axios";
import { handleUnauthorized } from "../../TokenAuth/auth";
import { formatUTCDateForDataGrid, formatUTCDateForExport, formatUTCDate, convertISTToUTCForAPI } from "../../utils/dateUtils";
import { useSearchParams } from "react-router-dom";
import { getIntParam, getStringParam, setIfTruthy } from "../../utils/urlState";

function DailyRechargeReport() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const RechargeData = useSelector((state) => state?.CallAndChatReducer || {});
  const { data: rowsData = [], paginationDetail = {}, status = "idle" } = RechargeData;

  const didInitUrlSyncRef = useRef(false);
  const applySawLoadingRef = useRef(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDate();

  const normalizeSearchType = (value) => {
    return value === "userName" || value === "mobileNumber" || value === "invoiceNumber"
      ? value
      : "userName";
  };

  const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [searchType, setSearchType] = useState(normalizeSearchType(getStringParam(searchParams, "searchType", "userName"))); // "userName", "mobileNumber", "invoiceNumber"
  const [appliedSearchType, setAppliedSearchType] = useState(normalizeSearchType(getStringParam(searchParams, "searchType", "userName")));
  const [statusFilter, setStatusFilter] = useState(getStringParam(searchParams, "status", "")); // "", "pending", "success", "failed"
  const [appliedStatusFilter, setAppliedStatusFilter] = useState(getStringParam(searchParams, "status", ""));
  const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
  const [appliedStartDate, setAppliedStartDate] = useState(getStringParam(searchParams, "startDate", ""));
  const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
  const [appliedEndDate, setAppliedEndDate] = useState(getStringParam(searchParams, "endDate", ""));
  const [isExporting, setIsExporting] = useState(false);
  const [lastAction, setLastAction] = useState(null); // "apply" to track when Apply Filters was clicked
  const isInitialMount = useRef(true);
  const lastParamsRef = useRef(null);

  const hasPendingFilterChanges =
    searchQuery.trim() !== (appliedSearchQuery || "") ||
    searchType !== (appliedSearchType || "userName") ||
    (startDate || "") !== (appliedStartDate || "") ||
    (endDate || "") !== (appliedEndDate || "") ||
    (statusFilter || "") !== (appliedStatusFilter || "");

  // Backward-compat: if an old URL used searchType=paymentStatus, map it to the new status dropdown.
  useEffect(() => {
    const urlSearchType = searchParams.get("searchType");
    const urlSearch = (searchParams.get("search") || "").trim();
    const urlStatus = (searchParams.get("status") || "").trim();
    if (urlSearchType === "paymentStatus" && urlSearch && !urlStatus) {
      setStatusFilter(urlSearch);
      setAppliedStatusFilter(urlSearch);
      setSearchQuery("");
      setAppliedSearchQuery("");
      setSearchType("userName");
      setAppliedSearchType("userName");
      setLastAction("apply");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If URL has explicit filters, treat them as applied.
  useEffect(() => {
    const hasAnyUrlFilter =
      Boolean(searchParams.get("search")) ||
      Boolean(searchParams.get("startDate")) ||
      Boolean(searchParams.get("endDate")) ||
      Boolean(searchParams.get("status"));
    if (hasAnyUrlFilter) setLastAction("apply");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply Filters Handler - Applies search and date filters together
  const handleApplyFilters = () => {
    setIsApplyingFilters(true);
    applySawLoadingRef.current = false;
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedSearchType(searchType);
    setAppliedStatusFilter(statusFilter);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setLastAction("apply");
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Apply button should stay disabled until request completes.
  useEffect(() => {
    if (!isApplyingFilters) return;
    if (status === "loading") {
      applySawLoadingRef.current = true;
      return;
    }
    if (status !== "loading" && applySawLoadingRef.current) {
      applySawLoadingRef.current = false;
      setIsApplyingFilters(false);
    }
  }, [status, isApplyingFilters]);

  // Keep URL in sync so Back restores pagination/filters.
  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }
    const params = new URLSearchParams();
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));
    setIfTruthy(params, "searchType", appliedSearchType);
    setIfTruthy(params, "search", appliedSearchQuery);
    setIfTruthy(params, "startDate", appliedStartDate);
    setIfTruthy(params, "endDate", appliedEndDate);
    setIfTruthy(params, "status", appliedStatusFilter);
    setSearchParams(params, { replace: true });
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    appliedSearchType,
    appliedSearchQuery,
    appliedStartDate,
    appliedEndDate,
    appliedStatusFilter,
    setSearchParams,
  ]);

  useEffect(() => {
    const page = paginationModel.page + 1;
    let limit = Number(paginationModel.pageSize);
    if (isNaN(limit)) {
      limit = 10000; // Use fixed default instead of paginationDetail
    }

    const params = {
      page: page,
      limit: limit,
    };

    // Date logic:
    // 1. If dates are applied (from Apply Filter), use them
    // 2. If no dates applied and it's initial load (lastAction is null), use today's date
    // 3. If no dates applied and Apply Filter was clicked (lastAction is "apply"), don't send dates
    if (appliedStartDate && appliedEndDate) {
      // Dates were explicitly applied - use them
      params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
      params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
    } else if (lastAction === null) {
      // Initial load or pagination change with no dates - send today's date by default
      params.startDate = convertISTToUTCForAPI(todayDate, 'start');
      params.endDate = convertISTToUTCForAPI(todayDate, 'end');
    }
    // If lastAction is "apply" and no dates, don't send dates (params won't have startDate/endDate)

    // Add search parameters if search query exists
    if (appliedSearchQuery && appliedSearchQuery.trim()) {
      params.search = appliedSearchQuery;
      params.searchType = appliedSearchType;
    }

    // Payment status filter
    if (appliedStatusFilter) {
      params.paymentStatus = appliedStatusFilter;
    }

    // Prevent duplicate API calls by comparing params
    const paramsString = JSON.stringify(params);
    if (lastParamsRef.current === paramsString && !isInitialMount.current) {
      return; // Skip if params haven't changed
    }

    lastParamsRef.current = paramsString;
    isInitialMount.current = false;

    dispatch(fetchRechargeReport(params));
  }, [dispatch, paginationModel.page, paginationModel.pageSize, appliedStartDate, appliedEndDate, appliedSearchQuery, appliedSearchType, appliedStatusFilter, lastAction, todayDate]);

  const columns = [
    {
      field: "sono",
      headerName: "S.No",
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    { field: "fullName", headerName: "User Name", width: 180 },
    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 150,
      renderCell: ({ row }) => (
        <Box
          sx={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'capitalize',
            display: 'inline-block',
            backgroundColor: row.paymentStatus === 'success' || row.paymentStatus === 'completed'
              ? 'rgba(76, 175, 80, 0.1)'
              : row.paymentStatus === 'pending' || row.paymentStatus === 'processing'
                ? 'rgba(255, 152, 0, 0.1)'
                : 'rgba(244, 67, 54, 0.1)',
            color: row.paymentStatus === 'success' || row.paymentStatus === 'completed'
              ? '#4caf50'
              : row.paymentStatus === 'pending' || row.paymentStatus === 'processing'
                ? '#ff9800'
                : '#f44336',
          }}
        >
          {row.paymentStatus || 'N/A'}
        </Box>
      ),
    },
    { field: "mobileNumber", headerName: "Mobile No.", width: 150 },
    { field: "dob", headerName: "DOB", width: 130 },
    { field: "gender", headerName: "Gender", width: 110 },
    { field: "address", headerName: "Address", width: 200 },
    { field: "city", headerName: "City", width: 130 },
    { field: "state", headerName: "State", width: 140 },
    { field: "couponCode", headerName: "Coupon Code", width: 140 },
    {
      field: "couponAmount",
      headerName: "Coupon Amount",
      width: 140,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    {
      field: "openingBalance",
      headerName: "Opening Balance",
      width: 160,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    {
      field: "rechargeAmount",
      headerName: "Recharge Amount",
      width: 160,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    {
      field: "promoAmount",
      headerName: "Promo Amount",
      width: 140,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    {
      field: "invoiceAmount",
      headerName: "Invoice Amount",
      width: 160,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    {
      field: "gst",
      headerName: "GST",
      width: 120,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    {
      field: "totalAmountWithGst",
      headerName: "Total Amount with GST",
      width: 180,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    {
      field: "totalWallet",
      headerName: "Total Wallet",
      width: 160,
      type: 'number',
      valueFormatter: (value) => `${value?.toLocaleString('en-IN') || '0'}`,
    },
    { field: "invoiceNumber", headerName: "Invoice No", width: 150 },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
      valueFormatter: formatUTCDateForDataGrid
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 180,
      valueFormatter: formatUTCDateForDataGrid
    },
  ];

  // Transform rows - no client-side filtering, server handles it
  const rows = useMemo(() => {
    const pageSize = paginationModel.pageSize;
    return rowsData
      ?.map((item, index) => ({
        ...item,
        id: item._id || item.id || index,
        sono: index + 1 + paginationModel.page * pageSize,
        fullName: item?.fullName || "N/A",
        mobileNumber: item?.mobileNumber || "N/A",
        dob: item?.dob ? (typeof item.dob === 'string' && item.dob.includes('T') ? item.dob.split('T')[0] : item.dob) : "N/A",
        gender: item?.gender || "N/A",
        address: item?.address || "N/A",
        city: item?.city || "N/A",
        state: item?.state || "N/A",
        couponCode: item?.couponCode || "N/A",
        couponAmount: item?.couponAmount || 0,
        openingBalance: item?.openingBalance || 0,
        rechargeAmount: item?.rechargeAmount || 0,
        promoAmount: item?.promoAmount || 0,
        totalWallet: item?.totalWallet || 0,
        gst: item?.gst || 0,
        invoiceNumber: item?.invoiceNumber || "N/A",
        totalAmountWithGst: item?.totalAmountWithGst || 0,
        invoiceAmount: item?.invoiceAmount || 0,
        paymentStatus: item?.paymentStatus || "N/A",
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));
  }, [rowsData, paginationModel]);

  // Pagination Handlers - same pattern as SessionReport.js
  const handleChangePage = (event, newPage) =>
    setPaginationModel((prev) => ({ ...prev, page: newPage }));

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    const newSize = parseInt(value, 10);
    setPaginationModel({ page: 0, pageSize: newSize });
  };

  // Reset Filter Handler
  const handleResetFilter = () => {
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setStatusFilter("");
    setAppliedStatusFilter("");
    setSearchQuery("");
    setAppliedSearchQuery("");
    setSearchType("userName");
    setAppliedSearchType("userName");
    setLastAction(null);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Export to CSV function - fetches all data from API
  const handleExport = async () => {
    setIsExporting(true);

    // Helper to properly format data for CSV (handles commas, quotes)
    const escapeCsvCell = (cell) => {
      if (cell === null || cell === undefined) return '';
      const cellString = String(cell);
      if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
        return `"${cellString.replace(/"/g, '""')}"`;
      }
      return cellString;
    };

    try {
      const token = localStorage.getItem("astroVani-admin-token");

      // Build query parameters for export - include search if present
      const params = new URLSearchParams();
      // Send dates - use today's date if not provided
      const exportStartDate = appliedStartDate || todayDate;
      const exportEndDate = appliedEndDate || todayDate;
      params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
      params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

      // Include search parameters in export if search was applied - map to direct field names
      if (appliedSearchQuery && appliedSearchQuery.trim()) {
        if (appliedSearchType === "userName") {
          params.append('userName', appliedSearchQuery);
        } else if (appliedSearchType === "mobileNumber") {
          params.append('mobileNumber', appliedSearchQuery);
        } else if (appliedSearchType === "invoiceNumber") {
          params.append('invoiceNumber', appliedSearchQuery);
        }
      }

      // Include status filter in export if applied
      if (appliedStatusFilter) {
        params.append('paymentStatus', appliedStatusFilter);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/rechargeReportExport?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized();
          throw new Error("Unauthorized: Please login again");
        }
        throw new Error("Failed to export data");
      }

      const result = await response.json();
      const allData = Array.isArray(result) ? result : result?.data || [];


      // Get the columns to export - exclude sono, createdAt, and updatedAt (we'll handle them separately)
      const exportableColumns = columns.filter(col => col.field !== 'sono' && col.field !== 'createdAt' && col.field !== 'updatedAt');

      // Create the header row - replace "Created At" with "Created Date" and "Created Time", and "Updated At" with "Updated Date" and "Updated Time"
      const headerRow = ['S.No', ...exportableColumns.map(col => escapeCsvCell(col.headerName)), 'Created Date', 'Created Time', 'Updated Date', 'Updated Time'].join(',');

      // Transform API data to match row structure
      const transformedData = allData.map((item, index) => {
        return {
          id: item._id || item.id || index,
          fullName: item?.fullName || "N/A",
          mobileNumber: item?.mobileNumber || "N/A",
          dob: item?.dob ? (typeof item.dob === 'string' && item.dob.includes('T') ? item.dob.split('T')[0] : item.dob) : "N/A",
          gender: item?.gender || "N/A",
          address: item?.address || "N/A",
          city: item?.city || "N/A",
          state: item?.state || "N/A",
          couponCode: item?.couponCode || "N/A",
          couponAmount: item?.couponAmount || 0,
          openingBalance: item?.openingBalance || 0,
          rechargeAmount: item?.rechargeAmount || 0,
          promoAmount: item?.promoAmount || 0,
          totalWallet: item?.totalWallet || 0,
          gst: item?.gst || 0,
          invoiceNumber: item?.invoiceNumber || "N/A",
          invoiceAmount: item?.invoiceAmount || 0,
          totalAmountWithGst: item?.totalAmountWithGst || 0,
          paymentStatus: item?.paymentStatus || "N/A",
          createdAt: item.createdAt || null,
          updatedAt: item.updatedAt || null,
        };
      });

      // Create data rows from API data
      const dataRows = transformedData.map((row, index) => {
        const sono = index + 1;
        const rowData = [
          sono,
          ...exportableColumns.map(col => {
            let value = row[col.field];
            // Format currency values
            if (col.valueFormatter && typeof value === 'number') {
              value = value.toLocaleString('en-IN');
            }
            return escapeCsvCell(value);
          }),
          // Add Created Date and Created Time separately
          escapeCsvCell(formatUTCDate(row.createdAt, { format: 'date', separator: '/' })),
          escapeCsvCell(formatUTCDate(row.createdAt, { format: 'time', showSeconds: true })),
          // Add Updated Date and Updated Time separately
          escapeCsvCell(formatUTCDate(row.updatedAt, { format: 'date', separator: '/' })),
          escapeCsvCell(formatUTCDate(row.updatedAt, { format: 'time', showSeconds: true }))
        ];
        return rowData.join(',');
      });

      // Combine header and data into a single CSV string
      const csvContent = [headerRow, ...dataRows].join('\n');

      // Create a Blob and trigger the browser download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `recharge-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
      alert(error.message || 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };


  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Recharge Report" />

        {/* Search and Export Toolbar */}
        <Box sx={{ width: '100%', mb: 2 }}>
          {/* Top Line: Search Items + Date Filters */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
            <FormControl size="small" sx={{ minWidth: 150, flexShrink: 0 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={statusFilter}
                label="Payment Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">pending</MenuItem>
                <MenuItem value="success">success</MenuItem>
                <MenuItem value="failed">failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchType}
                label="Search By"
                onChange={(e) => setSearchType(e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="userName">User Name</MenuItem>
                <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                <MenuItem value="invoiceNumber">Invoice Number</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder={
                searchType === "userName" ? "Search by user name..." :
                  searchType === "mobileNumber" ? "Search by mobile number..." :
                    "Search by invoice number..."
              }
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
              sx={{
                width: { xs: '150px', sm: '180px', md: '200px' },
                flexShrink: 0,
                background: 'var(--card-color)',
                borderRadius: '9px',
                '& .MuiInputBase-input': { fontSize: '13px', padding: '9px 10px 9px 0' },
              }}
            />
            <TextField
              label="Start Date"
              type="date"
              variant="outlined"
              size="small"
              value={startDate || ""}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '140px', sm: '160px', md: '180px' },
                flexShrink: 0,
                '& .MuiInputBase-input[type="date"]': {
                  paddingRight: '8px',
                  paddingLeft: '14px',
                  position: 'relative',
                  '&::-webkit-calendar-picker-indicator': {
                    cursor: 'pointer',
                    padding: '2px',
                    marginLeft: '0px',
                    marginRight: '4px',
                    opacity: 0.7,
                    position: 'absolute',
                    right: 0,
                    '&:hover': {
                      opacity: 1,
                    },
                  },
                },
                '& .MuiOutlinedInput-root': {
                  paddingRight: '8px',
                },
              }}
            />
            <TextField
              label="End Date"
              type="date"
              variant="outlined"
              size="small"
              value={endDate || ""}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '140px', sm: '160px', md: '180px' },
                flexShrink: 0,
                '& .MuiInputBase-input[type="date"]': {
                  paddingRight: '8px',
                  paddingLeft: '14px',
                  position: 'relative',
                  '&::-webkit-calendar-picker-indicator': {
                    cursor: 'pointer',
                    padding: '2px',
                    marginLeft: '0px',
                    marginRight: '4px',
                    opacity: 0.7,
                    position: 'absolute',
                    right: 0,
                    '&:hover': {
                      opacity: 1,
                    },
                  },
                },
                '& .MuiOutlinedInput-root': {
                  paddingRight: '8px',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={status === "loading" || isApplyingFilters || !hasPendingFilterChanges}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: "var(--primary-color, #1976d2)",
                color: "#fff",
                height: 40,
                px: 2,
                flexShrink: 0,
                "&:hover": { backgroundColor: "var(--primary-color, #1976d2)", opacity: 0.9 },
                "&:disabled": {
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              {isApplyingFilters ? "Applying..." : "Apply Filters"}
            </Button>
          </Box>
          {/* Bottom Line: Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={handleResetFilter}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: 'var(--primary-color, #1976d2)',
                color: 'var(--primary-color, #1976d2)',
                height: 40,
                px: 2,
                '&:hover': { borderColor: 'var(--primary-color, #1976d2)', backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                flexShrink: 0
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={isExporting}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                height: 40,
                px: 2,
                backgroundColor: "var(--primary-color, #1976d2)",
                color: "#fff",
                "&:hover": { backgroundColor: "var(--primary-color, #1976d2)", opacity: 0.9 },
                "&:disabled": { backgroundColor: "var(--primary-color, #1976d2)", opacity: 0.6 },
              }}
            >
              {isExporting ? (
                <>
                  <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                  Exporting...
                </>
              ) : (
                "Export"
              )}
            </Button>
          </Box>
        </Box>
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card" style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: 'none',
              overflow: 'hidden'
            }}>
              <div className="card-body" style={{ padding: 0 }}>
                {/* Custom Pagination on Top - same pattern as SessionReport.js */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                  }}
                >
                  <TablePagination
                    component="div"
                    count={paginationDetail?.totalRecords || 0}
                    page={paginationModel.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={paginationModel.pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </Box>

                <Box sx={{
                  width: '100%',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f8f9fa',
                  },
                }}>
                  <DataGrid
                    autoHeight
                    columns={columns}
                    rows={rows}
                    getRowId={(row) => row.id}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    disableSelectionOnClick
                    loading={status === "loading"}
                    hideFooterPagination
                    hideFooter
                    sx={{
                      '& .MuiDataGrid-cell:focus': { outline: 'none' },
                      '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    }}
                  />
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyRechargeReport;
